import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Sends focus back to the control that opened a dialog.
 *
 * Radix returns focus through `DialogTrigger`. These dialogs are opened by
 * plain buttons that set state, so Radix has no trigger to return to and focus
 * falls to the page, which sends a keyboard user back to the top.
 * Spread the result onto `DialogContent`.
 */
export function useFocusReturn(open: boolean) {
  const opener = useRef<HTMLElement | null>(null);

  // Layout effects run before Radix moves focus into the dialog, so the active
  // element is still the opener here.
  useLayoutEffect(() => {
    if (open) opener.current = document.activeElement as HTMLElement | null;
  }, [open]);

  const onCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    if (opener.current?.isConnected) opener.current.focus();
  }, []);

  return { onCloseAutoFocus };
}
